(() => {
  const FILTER_PARAM = 'filter.p.m.custom.product_ingredients';
  const LABEL_PARAM = 'ingredient_finder';
  const MIN_QUERY_LENGTH = 2;
  const MAX_SUGGESTIONS = 8;

  const normalise = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  const allProductsUrl = () => new URL('/collections/all', window.location.origin).toString();
  const indexUrl = () => `${allProductsUrl()}?view=ingredient-index`;

  const getIngredientIndex = async () => {
    if (window.__ingredientFinderIndex) return window.__ingredientFinderIndex;

    const firstUrl = indexUrl();

    const firstResponse = await fetch(firstUrl);
    if (!firstResponse.ok) throw new Error('Could not load the ingredient index.');

    const firstPage = await firstResponse.json();
    const pages = [firstPage];
    const pageCount = Number(firstPage.pages || 1);
    const requests = [];

    for (let page = 2; page <= pageCount; page += 1) {
      const url = new URL(firstUrl, window.location.origin);
      url.searchParams.set('page', page);
      requests.push(fetch(url.toString()).then((response) => {
        if (!response.ok) throw new Error('Could not load all ingredient pages.');
        return response.json();
      }));
    }

    if (requests.length) pages.push(...await Promise.all(requests));

    const grouped = new Map();
    pages.flatMap((page) => page.ingredients || []).forEach((ingredient) => {
      const label = String(ingredient.title || '').trim();
      const value = String(ingredient.value || '').trim();
      const key = normalise(label);
      if (!key || !value) return;

      if (!grouped.has(key)) grouped.set(key, { label, values: new Set() });
      grouped.get(key).values.add(value);
    });

    window.__ingredientFinderIndex = [...grouped.entries()].map(([key, item]) => ({
      key,
      label: item.label,
      values: [...item.values],
    })).sort((a, b) => a.label.localeCompare(b.label));

    return window.__ingredientFinderIndex;
  };

  const resultUrlForSelection = (ingredient) => {
    const current = new URL(window.location.href);
    const isSearchPage = current.pathname === '/search' || current.pathname.endsWith('/search');
    const target = isSearchPage
      ? new URL(allProductsUrl())
      : current;

    if (isSearchPage) {
      ['q', 'options[prefix]', 'type'].forEach((parameter) => target.searchParams.delete(parameter));
    }

    target.searchParams.delete(FILTER_PARAM);
    target.searchParams.set(LABEL_PARAM, ingredient.label);
    ingredient.values.forEach((value) => target.searchParams.append(FILTER_PARAM, value));
    return target.toString();
  };

  const resultUrlWithoutIngredient = () => {
    const target = new URL(window.location.href);
    target.searchParams.delete(FILTER_PARAM);
    target.searchParams.delete(LABEL_PARAM);
    return target.toString();
  };

  const initialise = (root) => {
    const input = root.querySelector('[data-ingredient-finder-input]');
    const suggestions = root.querySelector('[data-ingredient-finder-suggestions]');
    const status = root.querySelector('[data-ingredient-finder-status]');
    const chipHost = root.querySelector('[data-ingredient-finder-chip]');
    const selectedLabel = new URL(window.location.href).searchParams.get(LABEL_PARAM);
    let ingredients = [];

    if (selectedLabel) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'ingredient-finder__chip';
      chip.setAttribute('aria-label', `Remove ingredient filter: ${selectedLabel}`);

      const label = document.createElement('span');
      label.textContent = selectedLabel;

      const icon = document.createElement('span');
      icon.className = 'ingredient-finder__chip-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '×';

      chip.append(label, icon);
      chip.addEventListener('click', () => window.location.assign(resultUrlWithoutIngredient()));
      chipHost.appendChild(chip);
    }

    const clearSuggestions = () => {
      suggestions.innerHTML = '';
      suggestions.classList.remove('is-visible');
    };

    const renderSuggestions = (query) => {
      const normalisedQuery = normalise(query);
      clearSuggestions();
      if (normalisedQuery.length < MIN_QUERY_LENGTH) {
        status.textContent = normalisedQuery ? 'Keep typing to search ingredients.' : '';
        return;
      }

      const matches = ingredients.filter((ingredient) => ingredient.key.includes(normalisedQuery)).slice(0, MAX_SUGGESTIONS);
      if (!matches.length) {
        status.textContent = 'No matching ingredient found.';
        return;
      }

      status.textContent = '';
      matches.forEach((ingredient) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ingredient-finder__suggestion';
        button.textContent = ingredient.label;
        button.addEventListener('click', () => window.location.assign(resultUrlForSelection(ingredient)));
        item.appendChild(button);
        suggestions.appendChild(item);
      });
      suggestions.classList.add('is-visible');
    };

    input.addEventListener('input', () => renderSuggestions(input.value));
    input.addEventListener('focus', () => {
      if (input.value) renderSuggestions(input.value);
    });
    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) clearSuggestions();
    });

    getIngredientIndex()
      .then((loadedIngredients) => {
        ingredients = loadedIngredients;
        root.dataset.ingredientFinderReady = 'true';
      })
      .catch(() => {
        status.textContent = 'Ingredient finder is temporarily unavailable.';
        input.disabled = true;
      });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-ingredient-finder]').forEach(initialise);
  });
})();
